import { colors, Stack, Typography } from '@ui-components/neo.tax';
import { ReactComponent as AlarmBellRing } from '@ui-components/neo.tax/dist/icons/alarm-bell-1.svg';
import { useMenuState, Menu as AriaMenu } from 'ariakit';
import React, { ReactElement } from 'react';

import { useBackgroundOperations, useCurrentCompanyId } from '@neo.tax/shared/hooks';

import { MenuButton, StyledStack, MenuItemDivider, MenuItem, NotificationBadge } from './notification-menu.styled';
import { MemoizedNotificationMenuItem } from './NotificationMenuItem';

export const NotificationMenu = (): ReactElement | null => {
  const menuState = useMenuState({ gutter: 4 });
  const [hasNewItem, setHasNewItem] = React.useState(false);
  const companyId = useCurrentCompanyId();
  const { backgroundOperations, loading: loadingBackgroundOperations } = useBackgroundOperations();

  const backgroundOperationsRef = React.useRef(backgroundOperations);

  React.useEffect(() => {
    if (!backgroundOperationsRef.current && backgroundOperations) {
      backgroundOperationsRef.current = backgroundOperations;
    }
  }, [backgroundOperations]);

  React.useEffect(() => {
    backgroundOperationsRef.current = null;
    setHasNewItem(false);
  }, [companyId]);

  React.useEffect(() => {
    if (
      backgroundOperationsRef.current &&
      backgroundOperations &&
      backgroundOperations.length > backgroundOperationsRef.current.length
    ) {
      setHasNewItem(true);
      backgroundOperationsRef.current = backgroundOperations;
    }
  }, [backgroundOperations, backgroundOperationsRef]);

  React.useEffect(() => {
    if (menuState.open) {
      setHasNewItem(false);
    }
  }, [menuState.open]);

  return (
    <>
      <MenuButton state={menuState}>
        <AlarmBellRing width={18} color={colors.gray[400]} />
        {hasNewItem && <NotificationBadge />}
      </MenuButton>
      <StyledStack
        as={AriaMenu}
        state={menuState}
        direction="column"
        backgroundColor={colors.white}
        boxShadow="sm"
        borderRadius="md"
      >
        <Stack paddingX={2} paddingY={1} justifyContent="center" backgroundColor={colors.white} shadow="sm" fullWidth>
          <Typography color={colors.gray[600]} fontSize="xs" fontWeight="medium">
            In-progress
          </Typography>
        </Stack>
        <MenuItemDivider />
        {!backgroundOperations || !backgroundOperations.length ? (
          <MenuItem>
            <Stack paddingY={2} alignItems="center" justifyContent="center" fullWidth>
              <Typography fontSize="xs" fontWeight="medium" color={colors.gray[600]}>
                {loadingBackgroundOperations ? 'Loading...' : 'Nothing to see here'}
              </Typography>
            </Stack>
          </MenuItem>
        ) : (
          <>
            {backgroundOperations.map((item, index) => (
              <div key={item.id}>
                <MemoizedNotificationMenuItem item={item} />
                {index !== backgroundOperations.length - 1 ? <MenuItemDivider /> : null}
              </div>
            ))}
          </>
        )}
      </StyledStack>
    </>
  );
};
